import React from "react";
import { Link } from "wouter";

import Button, { ButtonTypes } from "../../../components/Button";
import { ResponsiveSelect } from "../../../components/MobileForm";
import { SelectSheetOption } from "../../../components/MobileForm/types";
import { Skeleton } from "../../../components/Skeleton";
import { EmptyState } from "../../../components/States";
import { SchedulerName } from "../../../services";
import { ObservedAt } from "../components/ObservedAt";
import { RefreshFailedNotice } from "../components/RefreshFailedNotice";
import { SectionUnavailable } from "../components/SectionUnavailable";
import { SCHEDULER_RUNS_NOTE } from "../constants";
import { useDetailNavigation } from "../hooks/useDetailNavigation";
import { ADMIN_PATH } from "../paths";
import {
  ActionLink,
  BackLink,
  Caption,
  Card,
  PageContent,
  PageHeader,
  PageHeading,
  PageTitle,
  QueueLayout,
} from "../styles";
import { plural } from "../utils/format";
import { SchedulerDetail } from "./components/SchedulerDetail";
import { SchedulerStatusList } from "./components/SchedulerStatusList";
import { FILTER_OPTIONS } from "./constants";
import { useSchedulers } from "./hooks/useSchedulers";
import { StatusFilter, Toolbar } from "./styles";
import { SchedulerFilter } from "./types";

export const SchedulersPage: React.FC = () => {
  const {
    section,
    filter,
    setFilter,
    hasSchedulers,
    visibleSchedulers,
    selected,
    select,
    isRefreshing,
    refresh,
  } = useSchedulers();
  const { isStacked, listRef, detailRef, openDetail, backToList } =
    useDetailNavigation();

  const isAvailable = section.state === "available";
  const isNothingRegistered = isAvailable && !hasSchedulers;
  const isNoMatch = isAvailable && hasSchedulers && selected === null;
  const refreshLabel = isRefreshing ? "Refreshing…" : "Refresh";

  const handleSelect = (name: SchedulerName) => {
    select(name);
    openDetail();
  };
  const handleFilterChange = ({ value }: SelectSheetOption) =>
    setFilter(value as SchedulerFilter);

  return (
    <PageContent>
      <BackLink>
        <Link href={ADMIN_PATH} asChild>
          <ActionLink>Back to operations overview</ActionLink>
        </Link>
      </BackLink>
      <PageHeader>
        <PageTitle>
          <PageHeading>Schedulers</PageHeading>
          <Caption>{SCHEDULER_RUNS_NOTE}</Caption>
          {isAvailable && <ObservedAt observedAt={section.observedAt} />}
        </PageTitle>
        <Button
          type={ButtonTypes.cancel}
          size="small"
          disabled={isRefreshing}
          onClick={refresh}
        >
          {refreshLabel}
        </Button>
      </PageHeader>
      {section.state === "loading" && (
        <Skeleton variant="rectangular" height="12rem" />
      )}
      {section.state === "unavailable" && (
        <Card aria-label="Schedulers">
          <SectionUnavailable
            observedAt={section.observedAt}
            onRetry={refresh}
          />
        </Card>
      )}
      {isAvailable && section.isRefreshFailed && (
        <RefreshFailedNotice sectionName="Schedulers" onRetry={refresh} />
      )}
      {isNothingRegistered && (
        <EmptyState
          title="No schedulers registered"
          description="Registered schedulers and their recorded runs will appear here."
        />
      )}
      {hasSchedulers && (
        <Toolbar>
          <StatusFilter>
            <ResponsiveSelect
              isCompact={isStacked}
              options={FILTER_OPTIONS}
              value={filter}
              onChange={handleFilterChange}
              sheetTitle="Status"
              testId="admin-scheduler-status"
            />
          </StatusFilter>
          <Caption>{plural(visibleSchedulers.length, "scheduler")}</Caption>
        </Toolbar>
      )}
      {isNoMatch && (
        <EmptyState
          title="No schedulers match"
          description="Choose another status to see recorded outcomes."
        />
      )}
      {selected && (
        <QueueLayout>
          <SchedulerStatusList
            schedulers={visibleSchedulers}
            selectedName={selected.name}
            listRef={listRef}
            onSelect={handleSelect}
          />
          <SchedulerDetail
            scheduler={selected}
            detailRef={detailRef}
            isStacked={isStacked}
            onBackToList={backToList}
          />
        </QueueLayout>
      )}
    </PageContent>
  );
};
